import React, { useMemo, useState, Fragment, Component } from 'react';
import { BuildingIcon, PlusIcon, Trash2Icon, TrendingUpIcon, DollarSignIcon, PieChartIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon, LockIcon, CheckCircleIcon, FileTextIcon, MailIcon, ArrowRightIcon, ArrowLeftIcon, AlertTriangleIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { PortfolioVisualMap } from './PortfolioVisualMap';
// --- Types ---
type LoanType = 'PI' | 'IO';
type PropertyType = 'PPOR' | 'Investment';
interface Loan {
  id: string;
  name: string;
  amount: number;
  rate: number;
  term: number;
  type: LoanType;
  securedAgainstId: string;
}
interface Renovation {
  enabled: boolean;
  cost: number;
  expectedUplift: number;
  rentIncrease: number;
}
interface Property {
  id: string;
  name: string;
  type: PropertyType;
  value: number;
  weeklyRent: number;
  annualExpenses: number;
  loans: Loan[];
  renovation: Renovation;
}
interface GlobalAssumptions {
  enableGrowth: boolean;
  growthRate: number;
  timeHorizon: number;
  enableRentalGrowth: boolean;
  rentalGrowthRate: number;
  expenseGrowthRate: number;
}
interface SellVsHoldAssumptions {
  yearsUntilSale: number;
  sellingCostsPercent: number;
}
const generateId = () => Math.random().toString(36).substr(2, 9);
const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0
}).format(val);
// --- Tooltip Component ---
const InfoTooltip = ({
  text
}: {
  text: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative inline-flex items-center justify-center ml-2 align-middle" data-id="element-915">
      <button type="button" onClick={e => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} onBlur={() => setIsOpen(false)} className="group relative flex items-center justify-center w-5 h-5 rounded-full hover:bg-slate-200 dark:hover:bg-white/10 transition-colors focus:outline-none" aria-label="More info" data-id="element-916">
        <InfoIcon className="w-4 h-4 text-slate-400 group-hover:text-blue-400 transition-colors" data-id="element-917" />
      </button>
      {isOpen && <>
          <div className="fixed inset-0 z-[99]" onClick={() => setIsOpen(false)} data-id="element-918" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[260px] p-3 bg-slate-800 text-xs text-slate-200 rounded-lg z-[100] shadow-xl border border-slate-700" data-id="element-919">
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-920"></div>
          </div>
        </>}
    </div>;
};
export function PortfolioCashflow() {
  // --- State ---
  const [properties, setProperties] = useState<Property[]>([{
    id: 'prop-1',
    name: 'Primary Residence (PPOR)',
    type: 'PPOR',
    value: 850000,
    weeklyRent: 0,
    annualExpenses: 4000,
    renovation: {
      enabled: false,
      cost: 0,
      expectedUplift: 0,
      rentIncrease: 0
    },
    loans: [{
      id: 'loan-1',
      name: 'Main Mortgage',
      amount: 500000,
      rate: 6.2,
      term: 30,
      type: 'PI',
      securedAgainstId: 'prop-1'
    }]
  }, {
    id: 'prop-2',
    name: 'Investment Property 1',
    type: 'Investment',
    value: 600000,
    weeklyRent: 550,
    annualExpenses: 7500,
    renovation: {
      enabled: false,
      cost: 50000,
      expectedUplift: 80000,
      rentIncrease: 100
    },
    loans: [{
      id: 'loan-2',
      name: 'IP1 Mortgage',
      amount: 480000,
      rate: 6.5,
      term: 30,
      type: 'IO',
      securedAgainstId: 'prop-2'
    }]
  }]);
  const [assumptions, setAssumptions] = useState<GlobalAssumptions>({
    enableGrowth: true,
    growthRate: 5,
    timeHorizon: 10,
    enableRentalGrowth: true,
    rentalGrowthRate: 3,
    expenseGrowthRate: 2
  });
  const [sellVsHold, setSellVsHold] = useState<SellVsHoldAssumptions>({
    yearsUntilSale: 5,
    sellingCostsPercent: 3
  });
  const [expandedPropId, setExpandedPropId] = useState<string | null>('prop-1');
  const [activeTab, setActiveTab] = useState<'setup' | 'visual' | 'projections' | 'strategy' | 'report'>('setup');
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [isStrategyUnlocked, setIsStrategyUnlocked] = useState(false);
  const [selectedForSale, setSelectedForSale] = useState<Record<string, boolean>>({});
  // Lead Funnel State
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    goal: '',
    consent: false
  });
  const [isReportUnlocked, setIsReportUnlocked] = useState(false);
  // --- Step definitions ---
  const steps = [{
    id: 'setup' as const,
    label: 'Properties & Loans',
    number: 1
  }, {
    id: 'visual' as const,
    label: 'Portfolio Map',
    number: 2
  }, {
    id: 'projections' as const,
    label: 'Growth Projections',
    number: 3
  }, {
    id: 'strategy' as const,
    label: 'Strategic Comparison',
    number: 4
  }, {
    id: 'report' as const,
    label: 'Report & Insights',
    number: 5
  }];
  const currentStepIndex = steps.findIndex(s => s.id === activeTab);
  const canNavigateToStep = (stepId: string) => {
    if (stepId === 'setup') return true;
    if (stepId === 'visual') return setupCompleted;
    if (stepId === 'projections') return setupCompleted;
    if (stepId === 'strategy') return setupCompleted;
    if (stepId === 'report') return setupCompleted;
    return false;
  };
  const goToNextStep = () => {
    if (activeTab === 'setup') {
      setSetupCompleted(true);
    }
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setActiveTab(steps[nextIndex].id);
    }
  };
  const goToPrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setActiveTab(steps[prevIndex].id);
    }
  };
  // --- Handlers ---
  const addProperty = () => {
    const newProp: Property = {
      id: generateId(),
      name: `Property ${properties.length + 1}`,
      type: 'Investment',
      value: 500000,
      weeklyRent: 500,
      annualExpenses: 6000,
      renovation: {
        enabled: false,
        cost: 0,
        expectedUplift: 0,
        rentIncrease: 0
      },
      loans: []
    };
    setProperties([...properties, newProp]);
    setExpandedPropId(newProp.id);
  };
  const removeProperty = (id: string) => setProperties(properties.filter(p => p.id !== id));
  const updateProperty = (id: string, field: keyof Property, value: any) => {
    setProperties(properties.map(p => p.id === id ? {
      ...p,
      [field]: value
    } : p));
  };
  const updateRenovation = (id: string, field: keyof Renovation, value: any) => {
    setProperties(properties.map(p => p.id === id ? {
      ...p,
      renovation: {
        ...p.renovation,
        [field]: value
      }
    } : p));
  };
  const addLoan = (propId: string) => {
    setProperties(properties.map(p => {
      if (p.id === propId) {
        return {
          ...p,
          loans: [...p.loans, {
            id: generateId(),
            name: `Loan ${p.loans.length + 1}`,
            amount: 100000,
            rate: 6.5,
            term: 30,
            type: 'IO',
            securedAgainstId: propId
          }]
        };
      }
      return p;
    }));
  };
  const removeLoan = (propId: string, loanId: string) => {
    setProperties(properties.map(p => p.id === propId ? {
      ...p,
      loans: p.loans.filter(l => l.id !== loanId)
    } : p));
  };
  const updateLoan = (propId: string, loanId: string, field: keyof Loan, value: any) => {
    setProperties(properties.map(p => {
      if (p.id === propId) {
        return {
          ...p,
          loans: p.loans.map(l => l.id === loanId ? {
            ...l,
            [field]: value
          } : l)
        };
      }
      return p;
    }));
  };
  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.consent) return;
    // Simulate API Call
    console.log('Lead Captured:', leadForm);
    console.log('Scenario Data:', {
      properties,
      assumptions,
      results
    });
    setIsReportUnlocked(true);
  };
  // --- Core Calculations ---
  const results = useMemo(() => {
    let totalValue = 0;
    let totalDebt = 0;
    let totalRent = 0;
    let totalExpenses = 0;
    let totalRepayments = 0;
    // Current Snapshot (incorporating active renovations)
    properties.forEach(p => {
      const effectiveValue = p.renovation.enabled ? p.value + p.renovation.expectedUplift : p.value;
      const effectiveRent = p.renovation.enabled ? p.weeklyRent + p.renovation.rentIncrease : p.weeklyRent;
      totalValue += effectiveValue;
      totalRent += effectiveRent * 52;
      totalExpenses += p.annualExpenses;
      p.loans.forEach(l => {
        totalDebt += l.amount;
        const r = l.rate / 100 / 12;
        const n = l.term * 12;
        let annualRepayment = 0;
        if (l.amount > 0 && r > 0) {
          if (l.type === 'PI' && n > 0) {
            const monthly = l.amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
            annualRepayment = monthly * 12;
          } else {
            annualRepayment = l.amount * (l.rate / 100);
          }
        }
        totalRepayments += annualRepayment;
      });
    });
    const totalEquity = totalValue - totalDebt;
    const netCashflow = totalRent - totalExpenses - totalRepayments;
    const portfolioLVR = totalValue > 0 ? totalDebt / totalValue * 100 : 0;
    // Projections
    const projections = [];
    let currentProps = properties.map(p => ({
      ...p,
      currentValue: p.renovation.enabled ? p.value + p.renovation.expectedUplift : p.value,
      currentRent: p.renovation.enabled ? p.weeklyRent + p.renovation.rentIncrease : p.weeklyRent,
      currentExpenses: p.annualExpenses,
      loansState: p.loans.map(l => ({
        ...l,
        currentBalance: l.amount
      }))
    }));
    for (let year = 0; year <= assumptions.timeHorizon; year++) {
      let yearValue = 0;
      let yearDebt = 0;
      let yearRent = 0;
      let yearExpenses = 0;
      let yearRepayments = 0;
      currentProps.forEach(p => {
        yearValue += p.currentValue;
        yearRent += p.currentRent * 52;
        yearExpenses += p.currentExpenses;
        p.loansState.forEach(l => {
          yearDebt += l.currentBalance;
          const r = l.rate / 100 / 12;
          const n = l.term * 12;
          let annualRepayment = 0;
          if (l.currentBalance > 0 && r > 0) {
            if (l.type === 'PI' && n > 0) {
              const monthly = l.amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
              annualRepayment = monthly * 12;
              for (let m = 0; m < 12; m++) {
                const interest = l.currentBalance * r;
                const principal = monthly - interest;
                l.currentBalance = Math.max(0, l.currentBalance - principal);
              }
            } else {
              annualRepayment = l.currentBalance * (l.rate / 100);
            }
          }
          yearRepayments += annualRepayment;
        });
        if (assumptions.enableGrowth) p.currentValue *= 1 + assumptions.growthRate / 100;
        if (assumptions.enableRentalGrowth) p.currentRent *= 1 + assumptions.rentalGrowthRate / 100;
        p.currentExpenses *= 1 + assumptions.expenseGrowthRate / 100;
      });
      projections.push({
        year,
        value: Math.round(yearValue),
        debt: Math.round(yearDebt),
        equity: Math.round(yearValue - yearDebt),
        netCashflow: Math.round(yearRent - yearExpenses - yearRepayments),
        rent: Math.round(yearRent),
        expenses: Math.round(yearExpenses),
        repayments: Math.round(yearRepayments)
      });
    }
    // --- Per-Property Sell vs Hold Calculations ---
    const yearsToSale = sellVsHold.yearsUntilSale;
    const costsPct = sellVsHold.sellingCostsPercent / 100;
    const perPropertyResults = properties.map(p => {
      const effectiveValue = p.renovation.enabled ? p.value + p.renovation.expectedUplift : p.value;
      const effectiveRent = p.renovation.enabled ? p.weeklyRent + p.renovation.rentIncrease : p.weeklyRent;
      const propDebt = p.loans.reduce((sum, l) => sum + l.amount, 0);
      // Project this property forward
      let futureValue = effectiveValue;
      let futureRent = effectiveRent;
      let futureExpenses = p.annualExpenses;
      let futureDebt = propDebt;
      // Track loan balances for P&I reduction
      const loanBalances = p.loans.map(l => ({
        ...l,
        balance: l.amount
      }));
      for (let yr = 0; yr < yearsToSale; yr++) {
        if (assumptions.enableGrowth) futureValue *= 1 + assumptions.growthRate / 100;
        if (assumptions.enableRentalGrowth) futureRent *= 1 + assumptions.rentalGrowthRate / 100;
        futureExpenses *= 1 + assumptions.expenseGrowthRate / 100;
        loanBalances.forEach(l => {
          if (l.type === 'PI' && l.balance > 0) {
            const r = l.rate / 100 / 12;
            const n = l.term * 12;
            if (r > 0 && n > 0) {
              const monthly = l.amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
              for (let m = 0; m < 12; m++) {
                const interest = l.balance * r;
                const principal = monthly - interest;
                l.balance = Math.max(0, l.balance - principal);
              }
            }
          }
        });
        futureDebt = loanBalances.reduce((sum, l) => sum + l.balance, 0);
      }
      // Calculate annual repayments at future state
      let futureRepayments = 0;
      loanBalances.forEach(l => {
        const r = l.rate / 100 / 12;
        const n = l.term * 12;
        if (l.balance > 0 && r > 0) {
          if (l.type === 'PI' && n > 0) {
            const monthly = l.amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
            futureRepayments += monthly * 12;
          } else {
            futureRepayments += l.balance * (l.rate / 100);
          }
        }
      });
      const futureCashflow = futureRent * 52 - futureExpenses - futureRepayments;
      const sellingCosts = futureValue * costsPct;
      const netProceeds = futureValue - sellingCosts - futureDebt;
      return {
        id: p.id,
        name: p.name,
        type: p.type,
        currentValue: effectiveValue,
        currentDebt: propDebt,
        currentEquity: effectiveValue - propDebt,
        futureValue: Math.round(futureValue),
        futureDebt: Math.round(futureDebt),
        futureEquity: Math.round(futureValue - futureDebt),
        futureCashflow: Math.round(futureCashflow),
        sellingCosts: Math.round(sellingCosts),
        netProceeds: Math.round(netProceeds),
        isSelectedForSale: !!selectedForSale[p.id]
      };
    });
    // Aggregate based on selections
    const sellProperties = perPropertyResults.filter(p => p.isSelectedForSale);
    const holdProperties = perPropertyResults.filter(p => !p.isSelectedForSale);
    const aggregateSell = {
      totalSaleValue: sellProperties.reduce((s, p) => s + p.futureValue, 0),
      totalSellingCosts: sellProperties.reduce((s, p) => s + p.sellingCosts, 0),
      totalDebtCleared: sellProperties.reduce((s, p) => s + p.futureDebt, 0),
      totalNetProceeds: sellProperties.reduce((s, p) => s + p.netProceeds, 0),
      cashflowRemoved: sellProperties.reduce((s, p) => s + p.futureCashflow, 0)
    };
    const aggregateHold = {
      totalValue: perPropertyResults.reduce((s, p) => s + p.futureValue, 0),
      totalDebt: perPropertyResults.reduce((s, p) => s + p.futureDebt, 0),
      totalEquity: perPropertyResults.reduce((s, p) => s + p.futureEquity, 0),
      totalCashflow: perPropertyResults.reduce((s, p) => s + p.futureCashflow, 0)
    };
    const remainingAfterSale = {
      totalValue: holdProperties.reduce((s, p) => s + p.futureValue, 0),
      totalDebt: holdProperties.reduce((s, p) => s + p.futureDebt, 0),
      totalEquity: holdProperties.reduce((s, p) => s + p.futureEquity, 0),
      totalCashflow: holdProperties.reduce((s, p) => s + p.futureCashflow, 0)
    };
    // Whole-portfolio hold (for backward compat with projections)
    const targetYear = projections[sellVsHold.yearsUntilSale] || projections[projections.length - 1];
    // Generate Insights
    const insights = [];
    if (portfolioLVR > 80) {
      insights.push('Your portfolio LVR is above 80%, which may reduce flexibility with some lenders and could incur LMI on future purchases.');
    } else if (portfolioLVR < 60) {
      insights.push('Your portfolio LVR is below 60%, indicating strong equity that may support future acquisitions, subject to lending capacity.');
    }
    if (netCashflow < 0) {
      insights.push('Your portfolio is currently negatively geared before tax. Ensure you have adequate cash buffers in place to support holding costs.');
    } else {
      insights.push('Your portfolio generates positive cashflow before tax, which may assist with servicing future debt.');
    }
    const hasRenovations = properties.some(p => p.renovation.enabled);
    if (hasRenovations) {
      insights.push('Your modeled renovations show an uplift in value and rent. This manufactured equity could be recycled for your next purchase.');
    }
    if (assumptions.enableGrowth && targetYear.equity > totalEquity) {
      insights.push(`Based on a ${assumptions.growthRate}% growth rate, your equity is projected to increase by ${formatCurrency(targetYear.equity - totalEquity)} over ${sellVsHold.yearsUntilSale} years.`);
    }
    return {
      totalValue,
      totalDebt,
      totalEquity,
      netCashflow,
      portfolioLVR,
      totalRent,
      totalExpenses,
      totalRepayments,
      projections,
      perPropertyResults,
      aggregateSell,
      aggregateHold,
      remainingAfterSale,
      sellVsHold: {
        targetYear: sellVsHold.yearsUntilSale,
        holdValue: targetYear.value,
        holdDebt: targetYear.debt,
        holdEquity: targetYear.equity,
        holdCashflow: targetYear.netCashflow,
        sellValue: targetYear.value,
        sellingCosts: targetYear.value * costsPct,
        netProceeds: targetYear.value - targetYear.value * costsPct - targetYear.debt
      },
      insights
    };
  }, [properties, assumptions, sellVsHold, selectedForSale]);
  return <div className="space-y-8" data-id="element-921">
      {/* Compliance Header */}
      <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-500/20 rounded-xl p-4 flex items-start gap-3" data-id="element-922">
        <InfoIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" data-id="element-923" />
        <div className="text-sm text-slate-700 dark:text-blue-200/80" data-id="element-924">
          <p className="font-medium text-primary-700 dark:text-blue-300 mb-1" data-id="element-925">
            General Information Only
          </p>
          <p data-id="element-926">
            This tool provides general information only and does not consider
            your personal financial situation, objectives or needs. All cashflow
            figures shown are <strong data-id="element-927">before tax</strong>. Tax has been excluded
            because outcomes vary depending on each person's circumstances.
            Scale Finance Strategies is not a tax accountant or financial
            planner, and this tool does not provide tax or financial advice.
          </p>
        </div>
      </div>

      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-928">
        <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-200 dark:border-white/10 relative overflow-hidden" data-id="element-929">
          <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-930">
            <BuildingIcon className="w-16 h-16" data-id="element-931" />
          </div>
          <div className="flex items-center mb-2" data-id="element-932">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-933">
              Total Portfolio Value
            </p>
            <InfoTooltip text="The estimated total market value of all properties in your portfolio, including any modeled renovation uplifts." data-id="element-934" />
          </div>
          <p className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-935">
            {formatCurrency(results.totalValue)}
          </p>
        </div>

        <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-200 dark:border-white/10 relative overflow-hidden" data-id="element-936">
          <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-937">
            <PieChartIcon className="w-16 h-16" data-id="element-938" />
          </div>
          <div className="flex items-center mb-2" data-id="element-939">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-940">
              Total Debt (LVR: {results.portfolioLVR.toFixed(1)}%)
            </p>
            <InfoTooltip text="Loan to Value Ratio (LVR). This shows how much debt you have relative to the value of your portfolio." data-id="element-941" />
          </div>
          <p className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-942">
            {formatCurrency(results.totalDebt)}
          </p>
        </div>

        <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-200 dark:border-white/10 relative overflow-hidden" data-id="element-943">
          <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-944">
            <TrendingUpIcon className="w-16 h-16" data-id="element-945" />
          </div>
          <div className="flex items-center mb-2" data-id="element-946">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-947">
              Total Equity
            </p>
            <InfoTooltip text="Equity is the difference between your total property value and what you owe. This is an indicative position only." data-id="element-948" />
          </div>
          <p className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-949">
            {formatCurrency(results.totalEquity)}
          </p>
        </div>

        <div className={`p-6 rounded-2xl border relative overflow-hidden ${results.netCashflow >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30' : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30'}`} data-id="element-950">
          <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-951">
            <DollarSignIcon className="w-16 h-16" data-id="element-952" />
          </div>
          <div className="flex items-center mb-2" data-id="element-953">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-954">
              Net Annual Cashflow
            </p>
            <InfoTooltip text="Rental income minus interest and expenses, strictly BEFORE TAX. Does not include principal repayments." data-id="element-955" />
          </div>
          <p className={`text-3xl font-bold ${results.netCashflow >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-956">
            {formatCurrency(results.netCashflow)}
          </p>
        </div>
      </div>

      {/* Step Progress Indicator */}
      <div className="relative" data-id="element-957">
        <div className="flex items-center justify-between" data-id="element-958">
          {steps.map((step, index) => {
          const isActive = activeTab === step.id;
          const isCompleted = currentStepIndex > index;
          const isAccessible = canNavigateToStep(step.id);
          return <Fragment key={step.id} data-id="element-959">
                <button onClick={() => isAccessible && setActiveTab(step.id)} disabled={!isAccessible} className={`flex flex-col items-center gap-2 group relative z-10 ${!isAccessible ? 'cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-960">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${isActive ? 'bg-gradient-to-r from-accent-500 to-primary-500 text-white shadow-[0_0_15px_rgba(20,184,166,0.3)]' : isCompleted ? 'bg-accent-500 text-white' : isAccessible ? 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-400 group-hover:bg-slate-300 dark:group-hover:bg-white/20' : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-600'}`} data-id="element-961">
                    {isCompleted ? <CheckCircleIcon className="w-5 h-5" data-id="element-962" /> : step.number}
                  </div>
                  <span className={`text-xs font-medium text-center hidden sm:block max-w-[100px] leading-tight ${isActive ? 'text-primary-600 dark:text-primary-400' : isCompleted ? 'text-slate-700 dark:text-slate-300' : isAccessible ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400 dark:text-slate-600'}`} data-id="element-963">
                    {step.label}
                  </span>
                  {!isAccessible && step.id === 'visual' && <LockIcon className="w-3 h-3 text-slate-400 dark:text-slate-600 absolute -top-1 -right-1" data-id="element-964" />}
                </button>
                {index < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded-full transition-all ${currentStepIndex > index ? 'bg-accent-500' : 'bg-slate-200 dark:bg-white/10'}`} data-id="element-965" />}
              </Fragment>;
        })}
        </div>
      </div>

      {/* --- TAB CONTENT --- */}
      <AnimatePresence mode="wait" data-id="element-966">
        {/* SETUP TAB */}
        {activeTab === 'setup' && <motion.div key="setup" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-967">
            <div className="flex justify-between items-center" data-id="element-968">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white" data-id="element-969">
                Portfolio Setup
              </h3>
              <button onClick={addProperty} className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-900 dark:text-white rounded-lg transition-colors text-sm font-medium border border-slate-200 dark:border-white/10" data-id="element-970">
                <PlusIcon className="w-4 h-4" data-id="element-971" /> Add Property
              </button>
            </div>

            <div className="space-y-4" data-id="element-972">
              {properties.map((prop, index) => <div key={prop.id} className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden" data-id="element-973">
                  {/* Property Header */}
                  <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-100 dark:hover:bg-white/[0.02] transition-colors" onClick={() => setExpandedPropId(expandedPropId === prop.id ? null : prop.id)} data-id="element-974">
                    <div className="flex items-center gap-4" data-id="element-975">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-976">
                        {index + 1}
                      </div>
                      <div data-id="element-977">
                        <h4 className="text-slate-900 dark:text-white font-medium flex items-center gap-2" data-id="element-978">
                          {prop.name}
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 uppercase tracking-wider" data-id="element-979">
                            {prop.type}
                          </span>
                        </h4>
                        <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-980">
                          Value: {formatCurrency(prop.value)} | Rent:{' '}
                          {formatCurrency(prop.weeklyRent)}/wk | Loans:{' '}
                          {prop.loans.length}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-981">
                      <button onClick={e => {
                  e.stopPropagation();
                  removeProperty(prop.id);
                }} className="p-2 text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors" data-id="element-982">
                        <Trash2Icon className="w-5 h-5" data-id="element-983" />
                      </button>
                      {expandedPropId === prop.id ? <ChevronUpIcon className="w-5 h-5 text-slate-400" data-id="element-984" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-985" />}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedPropId === prop.id && <div className="p-6 border-t border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-[#0f1629]/50 space-y-8" data-id="element-986">
                      {/* Property Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-987">
                        <div className="lg:col-span-2" data-id="element-988">
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-989">
                            Property Name
                          </label>
                          <input type="text" value={prop.name} onChange={e => updateProperty(prop.id, 'name', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-990" />
                        </div>
                        <div data-id="element-991">
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-992">
                            Type
                          </label>
                          <select value={prop.type} onChange={e => updateProperty(prop.id, 'type', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500 appearance-none" data-id="element-993">
                            <option value="PPOR" data-id="element-994">PPOR</option>
                            <option value="Investment" data-id="element-995">Investment</option>
                          </select>
                        </div>
                        <div data-id="element-996">
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-997">
                            Current Value ($)
                          </label>
                          <input type="number" value={prop.value} onChange={e => updateProperty(prop.id, 'value', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-998" />
                        </div>
                        <div data-id="element-999">
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-1000">
                            Weekly Rent ($)
                          </label>
                          <input type="number" value={prop.weeklyRent} onChange={e => updateProperty(prop.id, 'weeklyRent', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1001" />
                        </div>
                      </div>

                      {/* Renovation Toggle */}
                      <div className="bg-primary-50 dark:bg-blue-900/10 border border-primary-200 dark:border-blue-500/20 rounded-xl p-4" data-id="element-1002">
                        <div className="flex items-center justify-between mb-4" data-id="element-1003">
                          <div data-id="element-1004">
                            <h5 className="text-sm font-semibold text-primary-600 dark:text-primary-400 flex items-center" data-id="element-1005">
                              Renovation / Development Scenario
                              <InfoTooltip text="Model the impact of a renovation on your property's value and rental income." data-id="element-1006" />
                            </h5>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer" data-id="element-1007">
                            <input type="checkbox" checked={prop.renovation.enabled} onChange={e => updateRenovation(prop.id, 'enabled', e.target.checked)} className="sr-only peer" data-id="element-1008" />
                            <div className="w-11 h-6 bg-slate-300 dark:bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500" data-id="element-1009"></div>
                          </label>
                        </div>

                        {prop.renovation.enabled && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2" data-id="element-1010">
                            <div data-id="element-1011">
                              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-1012">
                                Cost of Works ($)
                              </label>
                              <input type="number" value={prop.renovation.cost} onChange={e => updateRenovation(prop.id, 'cost', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1013" />
                            </div>
                            <div data-id="element-1014">
                              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-1015">
                                Expected Value Uplift ($)
                              </label>
                              <input type="number" value={prop.renovation.expectedUplift} onChange={e => updateRenovation(prop.id, 'expectedUplift', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1016" />
                            </div>
                            <div data-id="element-1017">
                              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1" data-id="element-1018">
                                Expected Rent Increase ($/wk)
                              </label>
                              <input type="number" value={prop.renovation.rentIncrease} onChange={e => updateRenovation(prop.id, 'rentIncrease', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1019" />
                            </div>
                          </div>}
                      </div>

                      {/* Loans */}
                      <div data-id="element-1020">
                        <div className="flex justify-between items-center mb-4" data-id="element-1021">
                          <h5 className="text-sm font-semibold text-slate-600 dark:text-slate-300" data-id="element-1022">
                            Associated Loans / Splits
                          </h5>
                          <button onClick={() => addLoan(prop.id)} className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white rounded-md transition-colors text-xs font-medium" data-id="element-1023">
                            <PlusIcon className="w-3 h-3" data-id="element-1024" /> Add Split
                          </button>
                        </div>

                        {prop.loans.length === 0 ? <p className="text-sm text-slate-500 italic" data-id="element-1025">
                            No loans attached to this property.
                          </p> : <div className="space-y-3" data-id="element-1026">
                            {prop.loans.map(loan => <div key={loan.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-slate-50 dark:bg-white/5 p-3 rounded-xl border border-slate-100 dark:border-white/5" data-id="element-1027">
                                <div className="md:col-span-3" data-id="element-1028">
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1029">
                                    Loan Name
                                  </label>
                                  <input type="text" value={loan.name} onChange={e => updateLoan(prop.id, loan.id, 'name', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-2 py-1.5 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1030" />
                                </div>
                                <div className="md:col-span-2" data-id="element-1031">
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1032">
                                    Amount ($)
                                  </label>
                                  <input type="number" value={loan.amount} onChange={e => updateLoan(prop.id, loan.id, 'amount', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-2 py-1.5 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1033" />
                                </div>
                                <div className="md:col-span-1" data-id="element-1034">
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1035">
                                    Rate (%)
                                  </label>
                                  <input type="number" step="0.1" value={loan.rate} onChange={e => updateLoan(prop.id, loan.id, 'rate', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-2 py-1.5 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1036" />
                                </div>
                                <div className="md:col-span-1" data-id="element-1037">
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1038">
                                    Type
                                  </label>
                                  <select value={loan.type} onChange={e => updateLoan(prop.id, loan.id, 'type', e.target.value as LoanType)} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-2 py-1.5 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500 appearance-none" data-id="element-1039">
                                    <option value="PI" data-id="element-1040">P&I</option>
                                    <option value="IO" data-id="element-1041">IO</option>
                                  </select>
                                </div>
                                <div className="md:col-span-1" data-id="element-1042">
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1043">
                                    Term (Yr)
                                  </label>
                                  <input type="number" value={loan.term} onChange={e => updateLoan(prop.id, loan.id, 'term', Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-2 py-1.5 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500" data-id="element-1044" />
                                </div>
                                <div className="md:col-span-3" data-id="element-1045">
                                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1046">
                                    Secured Against
                                  </label>
                                  <select value={loan.securedAgainstId} onChange={e => updateLoan(prop.id, loan.id, 'securedAgainstId', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-2 py-1.5 text-slate-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500 appearance-none" data-id="element-1047">
                                    {properties.map(p => <option key={p.id} value={p.id} data-id="element-1048">
                                        {p.name}
                                      </option>)}
                                  </select>
                                </div>
                                <div className="md:col-span-1 flex justify-end pb-1" data-id="element-1049">
                                  <button onClick={() => removeLoan(prop.id, loan.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:text-slate-500 dark:hover:text-red-400 dark:hover:bg-red-400/10 rounded-md transition-colors" data-id="element-1050">
                                    <Trash2Icon className="w-4 h-4" data-id="element-1051" />
                                  </button>
                                </div>
                              </div>)}
                          </div>}
                      </div>
                    </div>}
                </div>)}
            </div>

            {/* Next Step Button */}
            <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-1052">
              <button onClick={goToNextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02] transition-all" data-id="element-1053">
                Continue to Portfolio Map <ArrowRightIcon className="w-5 h-5" data-id="element-1054" />
              </button>
            </div>
          </motion.div>}

        {/* VISUAL TAB */}
        {activeTab === 'visual' && <motion.div key="visual" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} data-id="element-1055">
            <PortfolioVisualMap properties={properties} onNavigateToSetup={() => setActiveTab('setup')} data-id="element-1056" />

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 mt-6 border-t border-slate-200 dark:border-white/10" data-id="element-1057">
              <button onClick={goToPrevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-1058">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1059" /> Properties & Loans
              </button>
              <button onClick={goToNextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02] transition-all" data-id="element-1060">
                Continue to Growth Projections{' '}
                <ArrowRightIcon className="w-5 h-5" data-id="element-1061" />
              </button>
            </div>
          </motion.div>}

        {/* PROJECTIONS TAB */}
        {activeTab === 'projections' && <motion.div key="projections" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-1062">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1063">
              <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-4" data-id="element-1064">
                <div className="flex items-center justify-between mb-3" data-id="element-1065">
                  <label className="text-sm font-medium text-slate-900 dark:text-white flex items-center" data-id="element-1066">
                    Enable Growth
                    <InfoTooltip text="Project future property values based on an estimated annual growth rate." data-id="element-1067" />
                  </label>
                  <input type="checkbox" checked={assumptions.enableGrowth} onChange={e => setAssumptions({
                ...assumptions,
                enableGrowth: e.target.checked
              })} className="rounded bg-slate-100 dark:bg-white/10 border-slate-300 dark:border-white/20 text-primary-500 focus:ring-primary-500" data-id="element-1068" />
                </div>
                <div className="space-y-3 opacity-100 transition-opacity" style={{
              opacity: assumptions.enableGrowth ? 1 : 0.5
            }} data-id="element-1069">
                  <div data-id="element-1070">
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1071">
                      Annual Growth Rate (%)
                    </label>
                    <input type="number" step="0.1" disabled={!assumptions.enableGrowth} value={assumptions.growthRate} onChange={e => setAssumptions({
                  ...assumptions,
                  growthRate: Number(e.target.value)
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-3 py-2 text-slate-900 dark:text-white text-sm" data-id="element-1072" />
                  </div>
                  <div data-id="element-1073">
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1074">
                      Time Horizon (Years)
                    </label>
                    <input type="number" disabled={!assumptions.enableGrowth} value={assumptions.timeHorizon} onChange={e => setAssumptions({
                  ...assumptions,
                  timeHorizon: Number(e.target.value)
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-3 py-2 text-slate-900 dark:text-white text-sm" data-id="element-1075" />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-4" data-id="element-1076">
                <div className="flex items-center justify-between mb-3" data-id="element-1077">
                  <label className="text-sm font-medium text-slate-900 dark:text-white flex items-center" data-id="element-1078">
                    Rental Growth
                    <InfoTooltip text="Project future rental income based on an estimated annual increase." data-id="element-1079" />
                  </label>
                  <input type="checkbox" checked={assumptions.enableRentalGrowth} onChange={e => setAssumptions({
                ...assumptions,
                enableRentalGrowth: e.target.checked
              })} className="rounded bg-slate-100 dark:bg-white/10 border-slate-300 dark:border-white/20 text-primary-500 focus:ring-primary-500" data-id="element-1080" />
                </div>
                <div className="space-y-3 opacity-100 transition-opacity" style={{
              opacity: assumptions.enableRentalGrowth ? 1 : 0.5
            }} data-id="element-1081">
                  <div data-id="element-1082">
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1083">
                      Annual Rental Growth (%)
                    </label>
                    <input type="number" step="0.1" disabled={!assumptions.enableRentalGrowth} value={assumptions.rentalGrowthRate} onChange={e => setAssumptions({
                  ...assumptions,
                  rentalGrowthRate: Number(e.target.value)
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-3 py-2 text-slate-900 dark:text-white text-sm" data-id="element-1084" />
                  </div>
                  <div data-id="element-1085">
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1086">
                      Annual Expense Inflation (%)
                    </label>
                    <input type="number" step="0.1" value={assumptions.expenseGrowthRate} onChange={e => setAssumptions({
                  ...assumptions,
                  expenseGrowthRate: Number(e.target.value)
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-md px-3 py-2 text-slate-900 dark:text-white text-sm" data-id="element-1087" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-white/5 p-4 sm:p-6 rounded-2xl border border-slate-200 dark:border-white/10 h-[350px] sm:h-[450px]" data-id="element-1088">
              <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-6" data-id="element-1089">
                Portfolio Value vs Debt Projection
              </h4>
              <ResponsiveContainer width="100%" height="100%" data-id="element-1090">
                <AreaChart data={results.projections} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-1091">
                  <defs data-id="element-1092">
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-1093">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-1094" />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-1095" />
                    </linearGradient>
                    <linearGradient id="colorDebt" x1="0" y1="0" x2="0" y2="1" data-id="element-1096">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} data-id="element-1097" />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} data-id="element-1098" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-1099" />
                  <XAxis dataKey="year" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={val => `Yr ${val}`} data-id="element-1100" />
                  <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={val => `${(val / 1000000).toFixed(1)}M`} data-id="element-1101" />
                  <Tooltip contentStyle={{
                backgroundColor: '#0f172a',
                borderColor: '#1e293b',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [formatCurrency(value), name]} labelFormatter={label => `Year ${label}`} data-id="element-1102" />
                  <Legend wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px'
              }} data-id="element-1103" />
                  <Area type="monotone" dataKey="value" name="Total Portfolio Value" stroke="#10b981" strokeWidth={3} fill="url(#colorValue)" data-id="element-1104" />
                  <Area type="monotone" dataKey="debt" name="Total Debt" stroke="#ef4444" strokeWidth={3} fill="url(#colorDebt)" data-id="element-1105" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-1106">
              <button onClick={goToPrevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-1107">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1108" /> Portfolio Map
              </button>
              <button onClick={goToNextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02] transition-all" data-id="element-1109">
                Continue to Strategic Comparison{' '}
                <ArrowRightIcon className="w-5 h-5" data-id="element-1110" />
              </button>
            </div>
          </motion.div>}

        {/* STRATEGY TAB */}
        {activeTab === 'strategy' && <motion.div key="strategy" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-1111">
            {!isStrategyUnlocked ? <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-12 text-center max-w-2xl mx-auto" data-id="element-1112">
                <LockIcon className="w-12 h-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" data-id="element-1113" />
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1114">
                  Unlock Strategic Comparison
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-8" data-id="element-1115">
                  Please confirm you have completed all required property, loan
                  and split details before running this strategic comparison.
                </p>
                <button onClick={() => setIsStrategyUnlocked(true)} className="px-8 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all" data-id="element-1116">
                  Confirm portfolio details are complete
                </button>
              </div> : <div className="space-y-6" data-id="element-1117">
                {/* Assumptions */}
                <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-6" data-id="element-1118">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4" data-id="element-1119">
                    Sell vs Hold Assumptions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1120">
                    <div data-id="element-1121">
                      <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-2" data-id="element-1122">
                        Years until sale
                      </label>
                      <input type="number" min="1" max={assumptions.timeHorizon} value={sellVsHold.yearsUntilSale} onChange={e => setSellVsHold({
                  ...sellVsHold,
                  yearsUntilSale: Number(e.target.value)
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500" data-id="element-1123" />
                    </div>
                    <div data-id="element-1124">
                      <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-2" data-id="element-1125">
                        Estimated selling costs (%)
                      </label>
                      <input type="number" step="0.1" value={sellVsHold.sellingCostsPercent} onChange={e => setSellVsHold({
                  ...sellVsHold,
                  sellingCostsPercent: Number(e.target.value)
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500" data-id="element-1126" />
                    </div>
                    <div className="flex items-end" data-id="element-1127">
                      <p className="text-xs text-slate-500 pb-2" data-id="element-1128">
                        Comparing position at Year {sellVsHold.yearsUntilSale}{' '}
                        based on a {assumptions.growthRate}% annual growth rate.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Per-Property Toggle Cards */}
                <div data-id="element-1129">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1130">
                    Select Properties to Sell
                    <InfoTooltip text="Toggle individual properties to model what your position could look like if you sold one or more. Properties not selected will be modeled as held." data-id="element-1131" />
                  </h3>
                  <div className="space-y-3" data-id="element-1132">
                    {results.perPropertyResults.map(prop => <div key={prop.id} className={`rounded-xl border p-4 transition-all cursor-pointer ${prop.isSelectedForSale ? 'bg-accent-50 dark:bg-cyan-500/10 border-accent-200 dark:border-cyan-500/30' : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/[0.07]'}`} onClick={() => setSelectedForSale(prev => ({
                ...prev,
                [prop.id]: !prev[prop.id]
              }))} data-id="element-1133">
                        <div className="flex items-center justify-between" data-id="element-1134">
                          <div className="flex items-center gap-4" data-id="element-1135">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${prop.isSelectedForSale ? 'bg-accent-100 dark:bg-cyan-500/20 text-accent-600 dark:text-cyan-400' : 'bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-400'}`} data-id="element-1136">
                              {prop.isSelectedForSale ? <DollarSignIcon className="w-5 h-5" data-id="element-1137" /> : <BuildingIcon className="w-5 h-5" data-id="element-1138" />}
                            </div>
                            <div data-id="element-1139">
                              <h4 className="text-slate-900 dark:text-white font-medium flex items-center gap-2" data-id="element-1140">
                                {prop.name}
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 uppercase tracking-wider" data-id="element-1141">
                                  {prop.type}
                                </span>
                              </h4>
                              <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-1142">
                                Current: {formatCurrency(prop.currentValue)} |
                                Projected ({sellVsHold.yearsUntilSale}yr):{' '}
                                {formatCurrency(prop.futureValue)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-1143">
                            <div className="text-right hidden sm:block" data-id="element-1144">
                              {prop.isSelectedForSale ? <div data-id="element-1145">
                                  <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-1146">
                                    Net Proceeds
                                  </p>
                                  <p className="text-accent-600 dark:text-cyan-400 font-bold" data-id="element-1147">
                                    {formatCurrency(prop.netProceeds)}
                                  </p>
                                </div> : <div data-id="element-1148">
                                  <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-1149">
                                    Future Equity
                                  </p>
                                  <p className="text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-1150">
                                    {formatCurrency(prop.futureEquity)}
                                  </p>
                                </div>}
                            </div>
                            <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider ${prop.isSelectedForSale ? 'bg-accent-100 dark:bg-cyan-500/20 text-accent-700 dark:text-cyan-300' : 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-400'}`} data-id="element-1151">
                              {prop.isSelectedForSale ? 'Sell' : 'Hold'}
                            </div>
                          </div>
                        </div>
                      </div>)}
                  </div>
                  {Object.values(selectedForSale).filter(Boolean).length === 0 && <p className="text-sm text-slate-500 mt-3 text-center" data-id="element-1152">
                      Click a property above to model selling it. You can select
                      one or more.
                    </p>}
                </div>

                {/* Aggregate Comparison */}
                {Object.values(selectedForSale).filter(Boolean).length > 0 && <div className="space-y-6" data-id="element-1153">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1154">
                      {/* Hold All Position */}
                      <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-6" data-id="element-1155">
                        <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2" data-id="element-1156">
                          <BuildingIcon className="w-5 h-5 text-primary-500 dark:text-blue-400" data-id="element-1157" />{' '}
                          Hold All Position
                          <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-auto" data-id="element-1158">
                            Year {sellVsHold.yearsUntilSale}
                          </span>
                        </h4>
                        <div className="space-y-4" data-id="element-1159">
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1160">
                            <span className="text-slate-600 dark:text-slate-400" data-id="element-1161">
                              Projected Portfolio Value
                            </span>
                            <span className="text-slate-900 dark:text-white font-medium" data-id="element-1162">
                              {formatCurrency(results.aggregateHold.totalValue)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1163">
                            <span className="text-slate-600 dark:text-slate-400" data-id="element-1164">
                              Projected Debt Position
                            </span>
                            <span className="text-red-600 dark:text-red-400 font-medium" data-id="element-1165">
                              {formatCurrency(results.aggregateHold.totalDebt)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1166">
                            <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1" data-id="element-1167">
                              Future Equity
                              <InfoTooltip text="Estimated equity if you hold all properties without selling." data-id="element-1168" />
                            </span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-bold text-lg" data-id="element-1169">
                              {formatCurrency(results.aggregateHold.totalEquity)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2" data-id="element-1170">
                            <span className="text-slate-600 dark:text-slate-400" data-id="element-1171">
                              Future Net Cashflow (Before Tax)
                            </span>
                            <span className={results.aggregateHold.totalCashflow >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} data-id="element-1172">
                              {formatCurrency(results.aggregateHold.totalCashflow)}{' '}
                              / yr
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Sell Selected Position */}
                      <div className="bg-slate-50 dark:bg-white/5 border border-accent-500/20 dark:border-cyan-500/20 rounded-2xl p-6" data-id="element-1173">
                        <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2" data-id="element-1174">
                          <DollarSignIcon className="w-5 h-5 text-accent-500 dark:text-cyan-400" data-id="element-1175" />{' '}
                          Sell Selected
                          <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-auto" data-id="element-1176">
                            {Object.values(selectedForSale).filter(Boolean).length}{' '}
                            of {properties.length} properties
                          </span>
                        </h4>
                        <div className="space-y-4" data-id="element-1177">
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1178">
                            <span className="text-slate-600 dark:text-slate-400" data-id="element-1179">
                              Projected Sale Value
                            </span>
                            <span className="text-slate-900 dark:text-white font-medium" data-id="element-1180">
                              {formatCurrency(results.aggregateSell.totalSaleValue)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1181">
                            <span className="text-slate-600 dark:text-slate-400" data-id="element-1182">
                              Estimated Selling Costs
                            </span>
                            <span className="text-orange-500 dark:text-orange-400 font-medium" data-id="element-1183">
                              -
                              {formatCurrency(results.aggregateSell.totalSellingCosts)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1184">
                            <span className="text-slate-600 dark:text-slate-400" data-id="element-1185">
                              Debt Cleared on Sale
                            </span>
                            <span className="text-red-600 dark:text-red-400 font-medium" data-id="element-1186">
                              -
                              {formatCurrency(results.aggregateSell.totalDebtCleared)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/5 pb-3" data-id="element-1187">
                            <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1" data-id="element-1188">
                              Potential Capital Released
                              <InfoTooltip text="An indication of capital that may be available to put toward another purchase. This is not a lending approval." data-id="element-1189" />
                            </span>
                            <span className="text-accent-600 dark:text-cyan-400 font-bold text-lg" data-id="element-1190">
                              {formatCurrency(results.aggregateSell.totalNetProceeds)}
                            </span>
                          </div>
                        </div>

                        {/* Remaining Portfolio After Sale */}
                        <div className="mt-6 pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-1191">
                          <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4" data-id="element-1192">
                            Remaining Portfolio After Sale
                          </h5>
                          <div className="space-y-3" data-id="element-1193">
                            <div className="flex justify-between items-center" data-id="element-1194">
                              <span className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-1195">
                                Remaining Value
                              </span>
                              <span className="text-slate-900 dark:text-white font-medium" data-id="element-1196">
                                {formatCurrency(results.remainingAfterSale.totalValue)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center" data-id="element-1197">
                              <span className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-1198">
                                Remaining Debt
                              </span>
                              <span className="text-red-600 dark:text-red-400 font-medium" data-id="element-1199">
                                {formatCurrency(results.remainingAfterSale.totalDebt)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center" data-id="element-1200">
                              <span className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-1201">
                                Remaining Equity
                              </span>
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium" data-id="element-1202">
                                {formatCurrency(results.remainingAfterSale.totalEquity)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center" data-id="element-1203">
                              <span className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-1204">
                                Remaining Cashflow
                              </span>
                              <span className={results.remainingAfterSale.totalCashflow >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} data-id="element-1205">
                                {formatCurrency(results.remainingAfterSale.totalCashflow)}{' '}
                                / yr
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Difference Summary */}
                    <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl p-6" data-id="element-1206">
                      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wider" data-id="element-1207">
                        Impact Summary
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-1208">
                        <div className="text-center" data-id="element-1209">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1210">
                            Equity Change
                          </p>
                          <p className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-1211">
                            {formatCurrency(results.remainingAfterSale.totalEquity + results.aggregateSell.totalNetProceeds - results.aggregateHold.totalEquity)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-1212">
                            Remaining equity + capital released vs hold all
                          </p>
                        </div>
                        <div className="text-center" data-id="element-1213">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1214">
                            Cashflow Change
                          </p>
                          <p className={`text-xl font-bold ${results.remainingAfterSale.totalCashflow - results.aggregateHold.totalCashflow >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-1215">
                            {formatCurrency(results.remainingAfterSale.totalCashflow - results.aggregateHold.totalCashflow)}{' '}
                            / yr
                          </p>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-1216">
                            Cashflow impact from selling selected properties
                          </p>
                        </div>
                        <div className="text-center" data-id="element-1217">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1" data-id="element-1218">
                            Capital Available
                          </p>
                          <p className="text-xl font-bold text-accent-600 dark:text-cyan-400" data-id="element-1219">
                            {formatCurrency(results.aggregateSell.totalNetProceeds)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-1220">
                            Indicative capital that may support a future
                            purchase
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}

                <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-500/20 rounded-xl p-4 flex items-start gap-3" data-id="element-1221">
                  <AlertTriangleIcon className="w-5 h-5 text-primary-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-1222" />
                  <p className="text-sm text-slate-700 dark:text-blue-200/80" data-id="element-1223">
                    <strong data-id="element-1224">Compliance Note:</strong> This is not a borrowing
                    assessment or credit approval. Lending capacity depends on
                    lender policy, income, liabilities, and personal
                    circumstances. Selling costs and capital gains tax (CGT)
                    have not been fully modeled and will impact actual net
                    proceeds.
                  </p>
                </div>
              </div>}

            {/* Navigation Buttons (always visible) */}
            <div className="flex justify-between pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-1225">
              <button onClick={goToPrevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-1226">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1227" /> Growth Projections
              </button>
              <button onClick={goToNextStep} className="px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold flex items-center gap-2 hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02] transition-all" data-id="element-1228">
                Continue to Report & Insights{' '}
                <ArrowRightIcon className="w-5 h-5" data-id="element-1229" />
              </button>
            </div>
          </motion.div>}

        {/* REPORT TAB */}
        {activeTab === 'report' && <motion.div key="report" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-8" data-id="element-1230">
            {!isReportUnlocked ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1231">
                <div data-id="element-1232">
                  <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-1233">
                    Get Your Detailed Portfolio Report
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-8" data-id="element-1234">
                    Unlock a comprehensive breakdown of your portfolio's current
                    position, 10-year projections, and strategic insights. We'll
                    email you a premium PDF summary.
                  </p>

                  <div className="space-y-4 mb-8" data-id="element-1235">
                    {['Full Cashflow Breakdown', 'Growth & Equity Projections', 'Sell vs Hold Analysis', 'Personalised Strategy Insights'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-1236">
                        <CheckCircleIcon className="w-5 h-5 text-accent-500 dark:text-cyan-400" data-id="element-1237" />
                        <span className="text-slate-700 dark:text-slate-300" data-id="element-1238">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-8" data-id="element-1239">
                  <form onSubmit={handleLeadSubmit} className="space-y-4" data-id="element-1240">
                    <div data-id="element-1241">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-1242">
                        Full Name
                      </label>
                      <input required type="text" value={leadForm.name} onChange={e => setLeadForm({
                  ...leadForm,
                  name: e.target.value
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500" data-id="element-1243" />
                    </div>
                    <div data-id="element-1244">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-1245">
                        Email Address
                      </label>
                      <input required type="email" value={leadForm.email} onChange={e => setLeadForm({
                  ...leadForm,
                  email: e.target.value
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500" data-id="element-1246" />
                    </div>
                    <div data-id="element-1247">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-1248">
                        Phone Number
                      </label>
                      <input required type="tel" value={leadForm.phone} onChange={e => setLeadForm({
                  ...leadForm,
                  phone: e.target.value
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500" data-id="element-1249" />
                    </div>
                    <div data-id="element-1250">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" data-id="element-1251">
                        Primary Goal
                      </label>
                      <select required value={leadForm.goal} onChange={e => setLeadForm({
                  ...leadForm,
                  goal: e.target.value
                })} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500 appearance-none" data-id="element-1252">
                        <option value="" data-id="element-1253">Select a goal...</option>
                        <option value="Buy next investment" data-id="element-1254">
                          Buy next investment
                        </option>
                        <option value="Refinance / restructure" data-id="element-1255">
                          Refinance / restructure
                        </option>
                        <option value="Release equity" data-id="element-1256">Release equity</option>
                        <option value="Improve cashflow" data-id="element-1257">
                          Improve cashflow
                        </option>
                        <option value="Renovate / develop" data-id="element-1258">
                          Renovate / develop
                        </option>
                        <option value="General strategy" data-id="element-1259">
                          General strategy
                        </option>
                      </select>
                    </div>
                    <div className="flex items-start gap-3 pt-2" data-id="element-1260">
                      <input required type="checkbox" id="consent" checked={leadForm.consent} onChange={e => setLeadForm({
                  ...leadForm,
                  consent: e.target.checked
                })} className="mt-1 rounded bg-slate-100 dark:bg-white/10 border-slate-300 dark:border-white/20 text-primary-500 focus:ring-primary-500" data-id="element-1261" />
                      <label htmlFor="consent" className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-1262">
                        I understand this report is general information only and
                        I agree to be contacted by Scale Finance Strategies
                        about my enquiry.
                      </label>
                    </div>
                    <button type="submit" className="w-full py-4 mt-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all flex items-center justify-center gap-2" data-id="element-1263">
                      <MailIcon className="w-5 h-5" data-id="element-1264" /> Email My Portfolio
                      Summary
                    </button>
                  </form>
                </div>
              </div> : <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1265">
                <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-2xl p-6 text-center" data-id="element-1266">
                  <CheckCircleIcon className="w-12 h-12 text-emerald-500 dark:text-emerald-400 mx-auto mb-3" data-id="element-1267" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-1268">
                    Report Generated Successfully
                  </h3>
                  <p className="text-emerald-700 dark:text-emerald-200/80" data-id="element-1269">
                    We've emailed a copy of this summary to {leadForm.email}.
                  </p>
                </div>

                {/* Strategy Insights Panel */}
                <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-8" data-id="element-1270">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2" data-id="element-1271">
                    <TrendingUpIcon className="w-6 h-6 text-primary-500 dark:text-blue-400" data-id="element-1272" />{' '}
                    Strategy Insights
                  </h3>
                  <div className="space-y-4" data-id="element-1273">
                    {results.insights.map((insight, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5" data-id="element-1274">
                        <div className="w-8 h-8 rounded-full bg-primary-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1275">
                          <ArrowRightIcon className="w-4 h-4 text-primary-500 dark:text-blue-400" data-id="element-1276" />
                        </div>
                        <p className="text-slate-700 dark:text-slate-300 leading-relaxed" data-id="element-1277">
                          {insight}
                        </p>
                      </div>)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1278">
                  <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-8 text-center" data-id="element-1279">
                    <FileTextIcon className="w-10 h-10 text-slate-400 mx-auto mb-4" data-id="element-1280" />
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-2" data-id="element-1281">
                      Download PDF Copy
                    </h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6" data-id="element-1282">
                      Save a local copy of your Portfolio Pulse Check.
                    </p>
                    <button className="px-6 py-3 rounded-xl bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/20 text-slate-900 dark:text-white font-medium transition-all w-full" data-id="element-1283">
                      Download Report
                    </button>
                  </div>
                  <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-blue-900/40 dark:to-cyan-900/40 border border-primary-200 dark:border-blue-500/20 rounded-2xl p-8 text-center" data-id="element-1284">
                    <BuildingIcon className="w-10 h-10 text-accent-500 dark:text-cyan-400 mx-auto mb-4" data-id="element-1285" />
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-2" data-id="element-1286">
                      Discuss Your Strategy
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-blue-200/80 mb-6" data-id="element-1287">
                      Book a session to discuss your structure, equity position
                      and next move.
                    </p>
                    <a href="#contact" className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all w-full" data-id="element-1288">
                      Book a Strategy Session
                    </a>
                  </div>
                </div>

                <p className="text-xs text-slate-500 text-center mt-8 max-w-3xl mx-auto" data-id="element-1289">
                  This report is general information only and does not
                  constitute credit, financial, taxation or investment advice.
                  Figures are estimates only and should not be relied on as a
                  substitute for personal advice.
                </p>
              </div>}

            {/* Back Button (no next since this is the last step) */}
            <div className="flex justify-start pt-6 border-t border-slate-200 dark:border-white/10" data-id="element-1290">
              <button onClick={goToPrevStep} className="px-6 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-1291">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1292" /> Strategic Comparison
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}